import React from "react";
import { Link } from "react-router-dom";
import {
  Users,
  FileText,
  GitBranch,
  Building2,
  ShieldCheck,
  Lock,
  Info,
  ArrowRight,
  CheckCircle2,
  Database,
  UserPlus
} from "lucide-react";

export default function EmployeeHubHRIS() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div 
      className="min-h-screen bg-white text-gray-800"
      style={robotoStyle}
    >
      {/* Hero Header Section */}
      <div className="bg-gradient-to-b from-emerald-50 to-white py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-6" style={poppinsStyle}>
            <Database size={16} />
            Centralized HRIS Platform
          </div>
          <h1 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight"
            style={poppinsStyle}
          >
            A Unified Hub for Your <br />
            <span className="text-emerald-600">Most Valuable Asset: People</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Eliminate fragmented data and manual spreadsheets. Employee Hub acts as the 
            single digital backbone for your organization, bringing together records, 
            workflows, and compliance into one secure, high-speed interface.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20">
        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-start mb-20">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-2" style={poppinsStyle}>
              <Info className="text-emerald-600" />
              Core Capabilities
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                  <UserPlus size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900" style={poppinsStyle}>Lifecycle Management</h4>
                  <p className="text-sm text-gray-600">Seamlessly manage onboarding, internal transfers, promotions, and exits without losing data history.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center text-cyan-600">
                  <Building2 size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900" style={poppinsStyle}>Org Structure</h4>
                  <p className="text-sm text-gray-600">Visualize reporting lines and department mappings that automatically sync across the entire suite.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
                  <Lock size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900" style={poppinsStyle}>Role-Based Security</h4>
                  <p className="text-sm text-gray-600">Granular permissions ensure that sensitive personal info and salary data are only visible to authorized personnel.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6" style={poppinsStyle}>Why Choose Our HRIS?</h2>
            <ul className="space-y-4">
              {[
                "Single source of truth for all employee data",
                "Automated document generation (Letters/Contracts)",
                "Audit-ready compliance tracking",
                "Real-time analytics and head-count reports",
                "Employee self-service portal access",
                "High-speed data retrieval & global search"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle2 className="text-emerald-500 flex-shrink-0 mt-1" size={18} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center py-10 border-t border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-2" style={poppinsStyle}>Ready to streamline your HR operations?</h3>
          <p className="text-gray-600 mb-8">Join 500+ companies scaling faster with our unified suite.</p>
          
          <div className="flex justify-center">
            {/* Reduced padding, cleaner background, links to /login */}
            <Link 
              to="/login"
              className="group flex items-center gap-3 bg-gray-900 hover:bg-emerald-600 text-white px-6 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-emerald-200/50"
            >
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Get Started</p>
                <p className="text-base font-semibold leading-none" style={poppinsStyle}>Access Dashboard</p>
              </div>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}